import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { loadFaceModels, detectFaceDescriptor, matchDescriptor, snapshotVideo } from "@/lib/faceapi";
import { Camera, SignIn, SignOut as SignOutIcon, ForkKnife, CheckCircle, WarningCircle, Hourglass, PaperPlaneRight } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const KIOSK_TOKEN_KEY = "hr_kiosk_device_token";
const publicApi = axios.create({ baseURL: `${API_BASE}/api` });
publicApi.interceptors.request.use((config) => {
  const t = localStorage.getItem(KIOSK_TOKEN_KEY);
  if (t) config.headers["X-Kiosk-Token"] = t;
  return config;
});

function PairingScreen({ onPaired }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("Tablet entrada");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await publicApi.post("/hr/kiosk/pair", { code: code.trim(), device_name: name.trim() || "Tablet" });
      localStorage.setItem(KIOSK_TOKEN_KEY, data.device_token);
      onPaired(data.device);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "No se pudo emparejar el dispositivo");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-[#0a1442] via-[#001a6a] to-[#000a25] text-white flex items-center justify-center p-6 font-sans" data-testid="kiosk-pairing">
      <form onSubmit={submit} className="w-full max-w-md bg-white/5 border border-white/15 rounded-2xl p-8 space-y-5 backdrop-blur">
        <div className="font-mono-data text-xs tracking-[0.4em] text-amber-400">ALENDRA · RH</div>
        <h1 className="font-heading text-3xl font-extrabold">Emparejar este dispositivo</h1>
        <p className="text-sm text-zinc-300">Por seguridad el reloj checador solo funciona en tablets autorizadas. Pide al administrador un código en <b>RH → Empleados → Kiosco</b> (vale 60 minutos, un solo uso).</p>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400">Código de emparejamiento</label>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="1234-5678" inputMode="numeric" autoFocus required
                 className="mt-1 w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-2xl font-mono-data tracking-widest text-center focus:outline-none focus:border-amber-400" data-testid="kiosk-pair-code" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400">Nombre del dispositivo</label>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
                 className="mt-1 w-full bg-black/30 border border-white/20 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" data-testid="kiosk-pair-name" />
        </div>
        {error && <div className="text-sm text-red-300 bg-red-500/10 border border-red-400/30 rounded-lg px-3 py-2" data-testid="kiosk-pair-error">{error}</div>}
        <button type="submit" disabled={busy || !code.trim()} className="w-full py-3 rounded-lg bg-amber-400 text-black font-bold disabled:opacity-50" data-testid="kiosk-pair-submit">
          {busy ? "Emparejando…" : "Emparejar"}
        </button>
      </form>
    </div>
  );
}

const TYPE_META = {
  in:        { label: "Entrada",        verb: "Tu entrada",        Icon: SignIn,       color: "#00873D" },
  lunch_out: { label: "Salida a comer", verb: "Tu salida a comer", Icon: ForkKnife,    color: "#FFC000" },
  lunch_in:  { label: "Regreso comida", verb: "Tu regreso",        Icon: ForkKnife,    color: "#1E88E5" },
  out:       { label: "Salida",         verb: "Tu salida",         Icon: SignOutIcon,  color: "#E0000F" },
};

export default function HrClockKiosk() {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [modelsReady, setModelsReady] = useState(false);
  const [streamReady, setStreamReady] = useState(false);
  const [pendingType, setPendingType] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [liveStatus, setLiveStatus] = useState("waiting"); // waiting|face|nobody
  const [now, setNow] = useState(new Date());
  const [paired, setPaired] = useState(() => !!localStorage.getItem(KIOSK_TOKEN_KEY));
  const [device, setDevice] = useState(null);
  const streamRef = useRef(null);
  const liveLoopRef = useRef(null);

  const unpair = useCallback(() => {
    localStorage.removeItem(KIOSK_TOKEN_KEY);
    setDevice(null);
    setPaired(false);
  }, []);

  // tick clock
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  // Init: load employees + models + camera
  useEffect(() => {
    if (!paired) return;
    let cancelled = false;
    (async () => {
      try {
        // Validar primero el token del dispositivo: si fue revocado → volver a emparejar.
        let sess = null;
        try {
          sess = await publicApi.get("/hr/kiosk/session").then((r) => r.data);
        } catch (err) {
          if (err?.response?.status === 401) { unpair(); return; }
        }
        const [emps] = await Promise.all([
          publicApi.get("/hr/kiosk/employees").then((r) => r.data),
          loadFaceModels(),
        ]);
        if (cancelled) return;
        if (sess?.device) setDevice(sess.device);
        setEmployees(emps);
        setModelsReady(true);
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 720, height: 540 } });
        if (cancelled) return;
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        setStreamReady(true);
        // Start live face detection loop for visual feedback
        startLiveLoop();
      } catch (err) {
        if (err?.response?.status === 401) { unpair(); return; }
        setFeedback({ ok: false, error: "No se pudo iniciar la cámara o modelos. Revisa permisos." });
      }
    })();
    return () => {
      cancelled = true;
      if (liveLoopRef.current) cancelAnimationFrame(liveLoopRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };

    function startLiveLoop() {
      let lastDetect = 0;
      const tick = async (t) => {
        liveLoopRef.current = requestAnimationFrame(tick);
        if (!videoRef.current || videoRef.current.readyState < 2) return;
        if (t - lastDetect < 250) return;          // ~4 fps for the live oval
        lastDetect = t;
        try {
          const faceapi = window.faceapi;
          const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 160, scoreThreshold: 0.5 });
          const det = await faceapi.detectSingleFace(videoRef.current, opts);
          drawOverlay(det);
          setLiveStatus(det ? "face" : "nobody");
        } catch { /* swallow */ }
      };
      liveLoopRef.current = requestAnimationFrame(tick);
    }

    function drawOverlay(det) {
      const canvas = overlayRef.current;
      const v = videoRef.current;
      if (!canvas || !v || !v.videoWidth) return;
      if (canvas.width !== v.videoWidth) { canvas.width = v.videoWidth; canvas.height = v.videoHeight; }
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Static oval guide
      const cx = canvas.width / 2, cy = canvas.height / 2;
      ctx.strokeStyle = det ? "rgba(0,230,118,.9)" : "rgba(255,255,255,.4)";
      ctx.lineWidth = 4;
      ctx.setLineDash(det ? [] : [12, 10]);
      ctx.beginPath();
      ctx.ellipse(cx, cy, canvas.width * 0.22, canvas.height * 0.32, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paired]);

  const handleCheck = useCallback(async (type) => {
    if (!modelsReady || !streamReady) return;
    setPendingType(type);
    setFeedback(null);
    try {
      const descriptor = await detectFaceDescriptor(videoRef.current);
      if (!descriptor) {
        setFeedback({ ok: false, type, error: "No se detectó un rostro. Mira a la cámara." });
        return;
      }
      const match = matchDescriptor(descriptor, employees, 0.55);
      if (!match) {
        setFeedback({ ok: false, type, error: "No reconocido. Acércate y mira a la cámara con buena luz." });
        return;
      }
      const photo = snapshotVideo(videoRef.current);
      try {
        const { data } = await publicApi.post("/hr/kiosk/clock", {
          employee_id: match.employee.id,
          type,
          photo_url: photo,
          source: "kiosk",
        });
        setFeedback({ ok: true, type, employee: data.employee, at: new Date(data.entry.at), distance: match.distance });
      } catch (err) {
        // 409 = fuera de ventana → mostrar UX para solicitar autorización
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 409 && typeof detail === "object" && detail.code === "out_of_window") {
          setFeedback({
            ok: false,
            type,
            error: detail.message,
            needsAuth: true,
            employee: match.employee,
            photo,
            scheduledTime: detail.scheduled_time,
          });
        } else {
          if (err?.response?.status === 401) { unpair(); return; }
          const msg = (typeof detail === "string" ? detail : detail?.message) || err.message || "Error";
          setFeedback({ ok: false, type, error: msg });
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Error";
      setFeedback({ ok: false, type, error: typeof msg === "string" ? msg : "Error" });
    } finally {
      setPendingType(null);
      // Auto-clear successful checks after 5s (gives time to read the overlay)
      setTimeout(() => setFeedback((f) => (f && f.ok ? null : f)), 5000);
    }
  }, [employees, modelsReady, streamReady, unpair]);

  const requestAuthorization = async (reason) => {
    if (!feedback?.needsAuth || !feedback.employee) return;
    try {
      await publicApi.post("/hr/kiosk/authorization-request", {
        employee_id: feedback.employee.id,
        requested_type: feedback.type,
        reason,
        photo_url: feedback.photo || "",
      });
      setFeedback({
        ok: false, type: feedback.type, isInfo: true,
        error: "Solicitud enviada al administrador. Espera su autorización.",
      });
      setTimeout(() => setFeedback(null), 6000);
    } catch (err) {
      setFeedback({ ok: false, type: feedback.type, error: err?.response?.data?.detail || "No se pudo enviar la solicitud" });
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-[#0a1442] via-[#001a6a] to-[#000a25] text-white overflow-hidden font-sans">
      {!paired && <PairingScreen onPaired={(d) => { setDevice(d); setPaired(true); }} />}
      {/* SUCCESS OVERLAY a pantalla completa */}
      {feedback?.ok && (
        <SuccessOverlay feedback={feedback} onClose={() => setFeedback(null)}/>
      )}
      <div className="absolute inset-0 grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
        {/* LEFT: clock + buttons */}
        <div className="flex flex-col justify-between">
          <header>
            <div className="font-mono-data text-xs tracking-[0.4em] text-amber-400 flex items-center gap-3">
              ALENDRA · RH
              {device && <span className="text-[10px] tracking-normal normal-case text-emerald-300 border border-emerald-400/40 rounded-full px-2 py-0.5" data-testid="kiosk-device-badge">● {device.name}</span>}
            </div>
            <h1 className="font-heading text-5xl lg:text-6xl font-extrabold mt-2">Reloj checador</h1>
            <p className="text-zinc-400 mt-2">Mira a la cámara y elige el tipo de checada.</p>
          </header>

          <div className="my-6">
            <div className="font-mono-data text-6xl lg:text-8xl font-extrabold tracking-tight tabular-nums">
              {now.toLocaleTimeString("es-MX", { timeZone: "America/Tijuana", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}
            </div>
            <div className="text-zinc-400 text-lg mt-2 capitalize">
              {now.toLocaleDateString("es-MX", { timeZone: "America/Tijuana", weekday: "long", day: "numeric", month: "long", year: "numeric" })} <span className="text-zinc-500 text-xs ml-1">· Tijuana</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-testid="clock-buttons">
            {Object.entries(TYPE_META).map(([k, meta]) => {
              const Icon = meta.Icon;
              const busy = pendingType === k;
              return (
                <button
                  key={k}
                  onClick={() => handleCheck(k)}
                  disabled={!streamReady || pendingType !== null}
                  className="group flex items-center gap-3 p-5 rounded-xl border-2 transition-all disabled:opacity-50"
                  style={{ background: meta.color + "22", borderColor: meta.color }}
                  data-testid={`clock-${k}`}
                >
                  <Icon size={32} weight="duotone" style={{ color: meta.color }}/>
                  <div className="text-left">
                    <div className="font-bold text-xl">{meta.label}</div>
                    <div className="text-[11px] text-zinc-300">{busy ? "Reconociendo…" : "Toca aquí"}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="text-xs text-zinc-500 mt-4">
            {modelsReady && streamReady && employees.length > 0 ? (
              <span>✓ {employees.length} empleados activos · Modelos cargados · Cámara lista</span>
            ) : (
              <span>Inicializando: {modelsReady ? "modelos ✓" : "modelos…"} · {streamReady ? "cámara ✓" : "cámara…"} · empleados {employees.length}</span>
            )}
          </div>
        </div>

        {/* RIGHT: camera + feedback */}
        <div className="flex flex-col gap-4">
          <div className="relative bg-black/40 rounded-2xl overflow-hidden aspect-video border-2 border-white/10">
            <video ref={videoRef} className="w-full h-full object-cover scale-x-[-1]" muted playsInline/>
            <canvas ref={overlayRef} className="absolute inset-0 w-full h-full pointer-events-none scale-x-[-1]"/>
            {!streamReady && (
              <div className="absolute inset-0 flex items-center justify-center text-zinc-400">
                <Camera size={48} weight="duotone"/> <span className="ml-2 text-sm">Esperando cámara…</span>
              </div>
            )}
            {streamReady && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                   style={{ background: liveStatus === "face" ? "rgba(0,230,118,.18)" : "rgba(255,255,255,.1)",
                            border: `1.5px solid ${liveStatus === "face" ? "#00E676" : "rgba(255,255,255,.3)"}`,
                            color: liveStatus === "face" ? "#00E676" : "#FFFFFF" }}>
                {liveStatus === "face" ? "✓ Rostro detectado" : "Acércate al óvalo"}
              </div>
            )}
          </div>

          {feedback && !feedback.ok && (
            <div
              className={`rounded-xl p-6 border-2 ${feedback.isInfo ? "bg-blue-500/15 border-blue-400" : "bg-red-500/15 border-red-400"}`}
              data-testid="clock-feedback"
            >
              {feedback.needsAuth ? (
                <AuthRequestPanel feedback={feedback} onRequest={requestAuthorization} onCancel={() => setFeedback(null)}/>
              ) : (
                <div className="flex items-center gap-3">
                  <WarningCircle size={32} weight="fill" className={feedback.isInfo ? "text-blue-300" : "text-red-300"}/>
                  <div>
                    <div className="text-2xl font-extrabold">{feedback.isInfo ? "Solicitud enviada" : "No registrado"}</div>
                    <div className={`${feedback.isInfo ? "text-blue-200" : "text-red-200"} text-sm`}>{feedback.error}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function AuthRequestPanel({ feedback, onRequest, onCancel }) {
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return;
    setSending(true);
    try { await onRequest(reason.trim()); }
    finally { setSending(false); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Hourglass size={28} weight="duotone" className="text-amber-300"/>
        <div>
          <div className="text-xl font-bold">Hola, {feedback.employee?.name}</div>
          <div className="text-red-200 text-sm">{feedback.error}</div>
        </div>
      </div>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Motivo: comida grupal, junta administrativa, etc."
        rows={2}
        className="w-full p-3 bg-black/30 text-white border border-white/20 rounded text-sm placeholder:text-zinc-500"
        data-testid="auth-reason"
      />
      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={sending || !reason.trim()}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg disabled:opacity-50"
          data-testid="auth-submit"
        >
          <PaperPlaneRight size={18} weight="fill"/>
          {sending ? "Enviando…" : "Solicitar autorización del admin"}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-3 border border-white/20 text-white/80 rounded-lg hover:bg-white/5"
          data-testid="auth-cancel"
        >
          Cancelar
        </button>
      </div>
      <p className="text-[11px] text-zinc-400">El admin recibirá tu solicitud. Una vez aprobada, tu checada quedará registrada automáticamente.</p>
    </div>
  );
}


function SuccessOverlay({ feedback, onClose }) {
  const meta = TYPE_META[feedback.type] || TYPE_META.in;
  const Icon = meta.Icon;
  const timeStr = feedback.at.toLocaleTimeString("es-MX", {
    timeZone: "America/Tijuana", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center backdrop-blur-xl animate-success-in"
      style={{ background: `radial-gradient(circle at center, ${meta.color}55 0%, ${meta.color}1A 50%, rgba(0,0,0,.7) 100%)` }}
      onClick={onClose}
      data-testid="kiosk-success-overlay"
    >
      <div className="text-center px-8 max-w-3xl">
        {/* Animated check icon */}
        <div
          className="w-40 h-40 mx-auto rounded-full flex items-center justify-center mb-6 shadow-2xl animate-success-pop"
          style={{ background: meta.color, boxShadow: `0 0 80px ${meta.color}99` }}
        >
          <CheckCircle size={96} weight="fill" className="text-white"/>
        </div>
        {/* Greeting */}
        <div className="font-mono-data text-sm tracking-[0.3em] text-white/70 mb-2">¡GRACIAS POR USAR EL KIOSCO!</div>
        <h1 className="font-heading text-6xl lg:text-7xl font-extrabold mb-4">
          ¡Hola, <span style={{ color: meta.color }}>{feedback.employee?.name?.split(" ")[0] || ""}</span>!
        </h1>
        {/* Action confirmed */}
        <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 border-2 mb-6"
             style={{ borderColor: meta.color }}>
          <Icon size={32} weight="duotone" style={{ color: meta.color }}/>
          <div className="text-left">
            <div className="text-sm text-white/70 uppercase tracking-wider">{meta.verb} quedó registrada</div>
            <div className="font-heading text-2xl font-bold">{meta.label}</div>
          </div>
        </div>
        {/* Time stamp */}
        <div className="font-mono-data text-5xl lg:text-6xl font-extrabold tabular-nums">{timeStr}</div>
        <div className="text-white/60 text-sm mt-2">
          {feedback.at.toLocaleDateString("es-MX", { timeZone: "America/Tijuana", weekday: "long", day: "numeric", month: "long" })} · Tijuana
        </div>
        {/* Hint */}
        <button onClick={onClose} className="mt-8 px-6 py-2 rounded-full border border-white/30 text-white/70 hover:bg-white/10 text-sm" data-testid="kiosk-success-close">
          Cerrar (se cierra solo en 5s)
        </button>
      </div>
    </div>
  );
}
